import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { authClient } from "../lib/auth";
import { de, type Dictionary } from "./de";
import { en } from "./en";

export type Locale = "de" | "en";
export const LOCALES: Locale[] = ["de", "en"];
const STORAGE_KEY = "fast360-locale";

const dictionaries: Record<Locale, Dictionary> = { de, en };

export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "en") return stored;
  } catch {
    /* ok */
  }
  return navigator.language?.toLowerCase().startsWith("de") ? "de" : "en";
}

const I18nContext = createContext<{
  locale: Locale;
  dict: Dictionary;
  setLocale: (l: Locale) => void;
  syncFromUser: (l: Locale) => void;
}>({ locale: "de", dict: de, setLocale: () => {}, syncFromUser: () => {} });

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(initial ?? detectLocale());

  const persistLocal = (l: Locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ok */
    }
  };

  /** Aktive Nutzerwahl: lokal + am eingeloggten User persistieren. */
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    persistLocal(l);
    void authClient.updateUser({ locale: l });
  }, []);

  /** Session geladen → gespeicherte User-Sprache übernehmen (ohne Server-Roundtrip). */
  const syncFromUser = useCallback((l: Locale) => {
    setLocaleState(l);
    persistLocal(l);
  }, []);

  return (
    <I18nContext.Provider value={{ locale, dict: dictionaries[locale], setLocale, syncFromUser }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useSyncLocaleFromUser(): (l: Locale) => void {
  return useContext(I18nContext).syncFromUser;
}

/** UI-Texte: `const t = useT(); t.dashboard.newTour` — vollständig typisiert. */
export function useT(): Dictionary {
  return useContext(I18nContext).dict;
}

export function useLocale(): [Locale, (l: Locale) => void] {
  const { locale, setLocale } = useContext(I18nContext);
  return [locale, setLocale];
}

export { apiError, pipelineError } from "./errors";
